/**
 * Event Indicator Day Component
 * Custom day cell for mini calendar that shows colored dots below dates with events
 */

import { Box } from '@mui/material'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { CalendarEventDto } from '@api/types/calendarEvent'
import { isSameDay, parseISO } from 'date-fns'

interface EventIndicatorDayProps extends PickersDayProps<Date> {
  events: CalendarEventDto[]
}

/**
 * Custom day cell that shows small dots below dates with events
 */
export function EventIndicatorDay(props: EventIndicatorDayProps) {
  const { events, day, outsideCurrentMonth, ...other } = props

  // Count events on this day
  const eventsOnDay = events.filter((event) => {
    const eventDate = parseISO(event.startDateTime)
    return isSameDay(eventDate, day)
  })

  const hasEvents = eventsOnDay.length > 0
  const eventCount = Math.min(eventsOnDay.length, 3) // Show max 3 dots

  return (
    <Box sx={{ position: 'relative' }}>
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(hasEvents && !outsideCurrentMonth && {
            fontWeight: 600,
          }),
        }}
      />
      {hasEvents && !outsideCurrentMonth && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 2,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.25,
          }}
        >
          {Array.from({ length: eventCount }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: 'primary.main',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default EventIndicatorDay
